import React from 'react';
import { Box } from '@mui/material';
import ButtonGrid from '../ButtonGrid';
import SliderWithInput from '../../ChartOptions/SliderWithInput';
import { SLIDER_CONFIGS } from './ButtonContent.constants';
import ColorPicker from './ColorPicker';
import AlignmentControls from './AlignmentControls';
import { IconProps, StateType, StateBasedProps } from './ButtonContent';
import { getDefaultColor, updateStateBasedProp, getStateBasedProp } from './ButtonContent.utils';

interface IconControlsProps {
  selectedImage: any;
  currentIconProps: IconProps;
  updateImage: (updates: any) => void;
  iconColorPickerOpen: boolean;
  iconProps: IconProps | StateBasedProps<IconProps>;
  activeState: StateType;
  setIconColorPickerOpen: (open: boolean) => void;
  isMultiContent?: boolean;
  iconColor?: string;
  backgroundColor?: string;
}

const IconControls: React.FC<IconControlsProps> = ({
  selectedImage,
  currentIconProps,
  updateImage,
  iconProps,
  activeState,
  iconColorPickerOpen,
  setIconColorPickerOpen,
  isMultiContent,
  iconColor,
  backgroundColor,
}) => {
  const updateIconProps = (updates: Partial<IconProps>) => {
    const newIconProps = updateStateBasedProp(iconProps, updates, activeState, 'icon');
    updateImage({ iconProps: newIconProps });
  };

  // Get the current icon based on active state
  const currentIcon = getStateBasedProp(
    typeof selectedImage.icon === 'object'
      ? selectedImage.icon
      : { default: selectedImage.icon || '' },
    activeState,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <Box
        sx={{
          width: '100%',
          mx: 'auto',
          pr: 1,
          pb: 1,
          borderTop: '1px solid lightgray',
          paddingTop: '10px',
          backgroundColor: backgroundColor,
          overflow: 'hidden',
        }}
      >
        <ButtonGrid
          onButtonSelect={(iconType, iconStyle) => {
            if (typeof selectedImage.icon === 'object' || activeState === 'hover') {
              const updatedIcon = updateStateBasedProp(
                typeof selectedImage.icon === 'object'
                  ? selectedImage.icon
                  : { default: selectedImage.icon || '' },
                iconType,
                activeState,
                undefined,
                true,
              );
              updateImage({
                icon: updatedIcon,
                iconStyle: iconStyle,
              });
            } else {
              updateImage({
                icon: iconType,
                iconStyle: iconStyle,
              });
            }
          }}
          selectedIcon={currentIcon}
          iconStyle={selectedImage.iconStyle}
          color={iconColor}
          backgroundColor={backgroundColor}
        />
      </Box>

      <ColorPicker
        label="Icon Color"
        lableColor={iconColor}
        color={getDefaultColor('icon', currentIconProps.color || '', activeState) as string}
        isOpen={iconColorPickerOpen}
        onToggle={() => setIconColorPickerOpen(!iconColorPickerOpen)}
        onColorChange={(newColor) => updateIconProps({ color: newColor })}
      />

      <div>
        <div className="optionDescription" style={{ marginBottom: '3px', color: iconColor }}>
          Icon Size
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={currentIconProps.fontSize ?? SLIDER_CONFIGS.iconSize.default}
          sliderMinMax={SLIDER_CONFIGS.iconSize}
          changeValue={(value: number) =>
            updateIconProps({
              fontSize: Math.max(
                SLIDER_CONFIGS.iconSize.min,
                Math.min(SLIDER_CONFIGS.iconSize.max, value),
              ),
            })
          }
        />
      </div>

      <AlignmentControls
        type="icon"
        iconColor={iconColor}
        currentProps={currentIconProps}
        updateProps={updateIconProps}
        isMultiContent={isMultiContent}
      />
    </div>
  );
};

export default IconControls;
